import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  History,
  Info,
  Loader2,
  Printer,
  RefreshCw,
  RotateCcw,
  Search,
  Table2,
  Users,
} from "lucide-react";
import { useEmployees } from "../../hooks/useEmployees";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import ReportsEnterpriseShell from "./components/ReportsEnterpriseShell";
import { useReportsNavigation } from "./useReportsNavigation";
import { PAYROLL_HUB_REPORTS } from "./PayrollReportsWorkspace";
import {
  exportEmployeeSalaryRegister,
  fetchEmployeeSalaryRegister,
} from "./employeeSalaryRegisterApi";
import { printRowsAsTable } from "./reportPrint";
import "../../styles/payrollReportsWorkspace.css";
import "../../styles/employeeSalaryRegister.css";

// Columns pinned while scrolling sideways, in order (each has its own left offset in CSS).
// employee_id (a long internal UUID) scrolls normally.
const STICKY_ORDER = ["serial_no", "employee_code", "employee_name"];
const stickyClass = (key) => {
  const idx = STICKY_ORDER.indexOf(key);
  return idx === -1 ? "" : `esr__sticky esr__sticky--${idx}`;
};
const PAGE_SIZE = 100;

const RESIGNED_HELP =
  "Include resigned: by default the register shows only employees who are active today. " +
  "Tick this to also show employees who have since resigned or become inactive, with their " +
  "salary for the selected month. Use it for a past month's complete register (audits, " +
  "PF / ESI / TDS filings, full & final settlement) so totals match what was actually paid."

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function EmployeeSalaryRegister() {
  const { departments } = useEmployees();
  const [theme, setTheme] = useState("light");
  const nav = useReportsNavigation({ payrollReports: PAYROLL_HUB_REPORTS });

  const [runId, setRunId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [search, setSearch] = useState("");
  const [includeResigned, setIncludeResigned] = useState(false);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const deptOptions = useMemo(() => {
    const map = new Map();
    (departments || []).forEach((d) => map.set(String(d.department_id || d.id), d.name || d.title));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [departments]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        include_resigned: includeResigned,
      };
      if (runId) params.payroll_run_id = runId;
      if (departmentId) params.department_id = departmentId;
      if (financialYear) params.financial_year_start = Number(financialYear);
      if (search.trim()) params.search = search.trim();

      const { data: payload } = await fetchEmployeeSalaryRegister(params);
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || "Failed to load register");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [runId, departmentId, financialYear, search, includeResigned, page]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => data?.columns || [], [data]);
  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const pagination = data?.pagination || {};

  const groupSpans = useMemo(() => {
    const spans = [];
    let i = 0;
    while (i < columns.length) {
      const label = columns[i].group_label || columns[i].group || "";
      let j = i + 1;
      while (j < columns.length && (columns[j].group_label || columns[j].group) === label) j++;
      spans.push({ label, count: j - i });
      i = j;
    }
    return spans;
  }, [columns]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = {
        format,
        include_resigned: includeResigned,
      };
      if (runId) params.payroll_run_id = runId;
      if (departmentId) params.department_id = departmentId;
      if (financialYear) params.financial_year_start = Number(financialYear);
      if (search.trim()) params.search = search.trim();

      const res = await exportEmployeeSalaryRegister(params);
      const ext = format === "xlsx" ? "xlsx" : format === "pdf" ? "pdf" : "csv";
      downloadBlob(res.data, `employee_salary_register.${ext}`);
    } catch (e) {
      setError("Export failed. Ensure a payroll run is processed and try again.");
    } finally {
      setExporting(null);
    }
  };

  const printRegister = () => {
    if (!rows.length) {
      setError("Generate the register before printing.");
      return;
    }
    // employee_id is an internal UUID: not useful on paper.
    const printCols = columns.filter((c) => c.key !== "employee_id");
    const label = (c) => c.label || c.key;
    const labels = printCols.map(label);
    const printable = rows.map((r) =>
      Object.fromEntries(printCols.map((c) => [label(c), r[c.key] ?? ""]))
    );
    // ~50 columns do not fit one sheet: print in parts, repeating the pinned columns.
    const keyColumns = STICKY_ORDER.map((k) => printCols.find((c) => c.key === k))
      .filter(Boolean)
      .map(label);
    printRowsAsTable({
      keyColumns,
      title: "Employee Salary Register",
      subtitle: [
        data?.organisation?.name ? `Organisation: ${data.organisation.name}` : null,
        `Rows: ${pagination.total_rows ?? rows.length}`,
      ]
        .filter(Boolean)
        .join("  |  "),
      columns: labels,
      rows: printable,
    });
  };

  const moneyCols = useMemo(
    () => new Set(columns.filter((c) => c.type === "money").map((c) => c.key)),
    [columns]
  );

  return (
    <div className="prw esr-page" data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey="employee_salary_register"
        selectedReport={nav.selectedReport}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={nav.onSelectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle="Salary Register"
      >
    <div className="prw__body">
    <div className="prw__standard esr">
      {/* Page header — same layout as the other report pages */}
      <div className="prw__page-header">
        <div className="prw__page-header-main">
          <div className="prw__page-icon">
            <Table2 size={26} />
          </div>
          <div>
            <h1 className="prw__page-title">Employee Salary Register</h1>
            <p className="prw__page-desc">
              Enterprise monthly payroll statement — immutable snapshot from processed payroll (not
              individual payslips).
            </p>
            <div className="prw__page-meta">
              <span className="prw__meta-chip">
                <Building2 size={13} /> Organisation: <b>{data?.organisation?.name || "—"}</b>
              </span>
              <span className="prw__meta-chip">
                <History size={13} /> Generated:{" "}
                <b>{data?.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}</b>
              </span>
              <span className="prw__meta-chip">
                <Users size={13} /> Rows: <b>{pagination.total_rows ?? rows.length}</b>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter card */}
      <div className="prw__filter-card">
        <p className="prw__filter-card-title">
          <Filter size={16} /> Report Filters
        </p>
        <div className="prw__filter-grid">
          <div className="prw__filter">
            <PayrollRunSelect
              label="Payroll Month"
              value={runId}
              onChange={(id) => {
                setRunId(id);
                setPage(1);
              }}
              allowEmpty
              emptyLabel="Select payroll month"
              hint=""
            />
          </div>
          <div className="prw__filter">
            <label htmlFor="esr-fy">
              <CalendarDays size={12} /> Financial Year
            </label>
            <select
              id="esr-fy"
              value={financialYear}
              onChange={(e) => {
                setFinancialYear(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Years</option>
              {[2026, 2025, 2024, 2023].map((y) => (
                <option key={y} value={y}>
                  FY {y}–{String(y + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
          <div className="prw__filter">
            <label htmlFor="esr-dept">
              <Building2 size={12} /> Department
            </label>
            <select
              id="esr-dept"
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Departments</option>
              {deptOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="prw__filter">
            <label htmlFor="esr-search">
              <Search size={12} /> Search
            </label>
            <input
              id="esr-search"
              type="search"
              placeholder="Name, code, UAN, PF…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
          <div className="prw__filter esr__resigned">
            <label htmlFor="esr-resigned">
              <Users size={12} /> Employees
              <span
                className="esr__info"
                title={RESIGNED_HELP}
                aria-label={RESIGNED_HELP}
                role="img"
              >
                <Info size={13} />
              </span>
            </label>
            <label className="esr__check" htmlFor="esr-resigned">
              <input
                id="esr-resigned"
                type="checkbox"
                aria-describedby="esr-resigned-hint"
                checked={includeResigned}
                onChange={(e) => {
                  setIncludeResigned(e.target.checked);
                  setPage(1);
                }}
              />
              Include resigned
            </label>
            <p className="esr__hint" id="esr-resigned-hint">
              Shows employees who have left (resigned / inactive). Tick it for a past month&apos;s
              complete register.
            </p>
          </div>
          <div className="prw__filter-actions">
            <button
              type="button"
              className="prw__btn prw__btn--generate"
              onClick={load}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Generate
            </button>
            <button
              type="button"
              className="prw__btn prw__btn--ghost"
              onClick={() => {
                setRunId("");
                setFinancialYear("");
                setDepartmentId("");
                setSearch("");
                setIncludeResigned(false);
                setPage(1);
              }}
            >
              <RotateCcw size={16} /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Action toolbar */}
      <div className="prw__toolbar">
        <button
          type="button"
          className="prw__btn prw__btn--refresh prw__btn--sm"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          type="button"
          className="prw__btn prw__btn--excel prw__btn--sm"
          onClick={() => handleExport("xlsx")}
          disabled={Boolean(exporting)}
        >
          <FileSpreadsheet size={14} /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
        </button>
        <button
          type="button"
          className="prw__btn prw__btn--pdf prw__btn--sm"
          onClick={() => handleExport("pdf")}
          disabled={Boolean(exporting)}
        >
          <FileText size={14} /> {exporting === "pdf" ? "Exporting…" : "PDF"}
        </button>
        <button
          type="button"
          className="prw__btn prw__btn--csv prw__btn--sm"
          onClick={() => handleExport("csv")}
          disabled={Boolean(exporting)}
        >
          <Download size={14} /> {exporting === "csv" ? "Exporting…" : "CSV"}
        </button>
        <button type="button" className="prw__btn prw__btn--print prw__btn--sm" onClick={printRegister}>
          <Printer size={14} /> Print
        </button>
      </div>

      {error ? <div className="esr__alert">{error}</div> : null}

      <div className="esr__table-wrap">
        {loading && !rows.length ? (
          <div className="esr__empty">
            <Loader2 className="animate-spin" size={28} />
            <div style={{ marginTop: 8 }}>Loading salary register…</div>
          </div>
        ) : !rows.length ? (
          <div className="esr__empty">
            <Table2 size={32} style={{ opacity: 0.3 }} />
            <div style={{ marginTop: 8, fontWeight: 600 }}>No payroll data</div>
            <div style={{ fontSize: 13 }}>
              Process payroll for a month, then select that payroll run above.
            </div>
          </div>
        ) : (
          <table className="esr__table">
            <thead>
              <tr className="esr__group-row">
                {groupSpans.map((g) => (
                  <th key={g.label} colSpan={g.count}>
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr className="esr__col-row">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={stickyClass(col.key) || undefined}
                    title={col.label}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.employee_id}-${row.payroll_run_id}-${row.serial_no}`}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={[
                        stickyClass(col.key),
                        moneyCols.has(col.key) ? "esr__num" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {row[col.key] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.total_pages > 1 ? (
        <div className="esr__pagination">
          <span>
            Page {pagination.page} of {pagination.total_pages} ({pagination.total_rows} employees)
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="esr__btn"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="esr__btn"
              disabled={page >= pagination.total_pages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {summary && rows.length ? (
        <div className="esr__footer">
          <h3>Footer Summary</h3>
          <div className="esr__footer-grid">
            <div>
              <span>Total Employees</span>
              <strong>{summary.total_employees}</strong>
            </div>
            <div>
              <span>Regular Employees</span>
              <strong>{summary.regular_employees}</strong>
            </div>
            <div>
              <span>Contract Employees</span>
              <strong>{summary.contract_employees}</strong>
            </div>
            <div>
              <span>Grand Net Salary Paid</span>
              <strong>₹ {summary.grand_net_salary_paid}</strong>
            </div>
            <div>
              <span>Total Gross Pay</span>
              <strong>₹ {summary.total_gross_pay}</strong>
            </div>
            <div>
              <span>Total PF (Employee)</span>
              <strong>₹ {summary.total_pf_employee}</strong>
            </div>
            <div>
              <span>Total ESI (Employee)</span>
              <strong>₹ {summary.total_esi_employee}</strong>
            </div>
            <div>
              <span>Total TDS</span>
              <strong>₹ {summary.total_tds}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </div>
    </ReportsEnterpriseShell>
    </div>
  );
}
